<template>
  <!-- Rule 管理：通过数据库元信息构建结构化 db_assert 规则。 -->
  <section class="view rules-view">
    <div class="split-grid compact rules-workspace">
      <section class="surface rule-list-surface">
        <div class="surface-head">
          <div>
            <h2>Rule 列表</h2>
            <span>{{ allRules.length }} 条规则</span>
          </div>
          <section class="surface-actions">
            <el-button size="small" type="primary" :icon="icons.Plus" @click="handleAddRule">
              添加规则
            </el-button>
            <el-button size="small" :icon="icons.Refresh" @click="loadAllRules">刷新</el-button>
          </section>
        </div>

        <el-table
          :data="allRules"
          row-key="id"
          class="rule-table"
          height="640"
          highlight-current-row
          :row-class-name="ruleRowClassName"
          @row-click="handleRuleRowClick"
        >
          <el-table-column label="Rule" prop="id" width="82" />
          <el-table-column label="Job 描述" min-width="180">
            <template #default="{ row }">
              <span class="rule-target-text">{{ ruleJobLabel(row) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="目标表" min-width="210">
            <template #default="{ row }">
              <div v-if="ruleTargetParts(row).length" class="rule-path compact">
                <span v-for="part in ruleTargetParts(row)" :key="part">{{ part }}</span>
              </div>
              <span v-else class="rule-target-text">{{ ruleTarget(row) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="筛选条件" min-width="210">
            <template #default="{ row }">
              <span class="rule-target-text">{{ ruleFilter(row) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="断言" min-width="180">
            <template #default="{ row }">{{ ruleAssertion(row) }}</template>
          </el-table-column>
          <el-table-column label="更新时间" width="168">
            <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="306" fixed="right">
            <template #default="{ row }">
              <el-button size="small" :icon="icons.Edit" @click.stop="fillRuleForm(row)">
                编辑
              </el-button>
              <el-button
                size="small"
                :icon="icons.CaretRight"
                :loading="runningRuleId === row.id"
                @click.stop="handleTestSavedRule(row)"
              >
                测试
              </el-button>
              <el-button
                size="small"
                type="primary"
                plain
                :icon="icons.Share"
                :loading="sharingRuleTemplateId === Number(row.id)"
                @click.stop="handleShareRule(row.id)"
              >
                分享
              </el-button>
              <el-button
                size="small"
                type="danger"
                plain
                :icon="icons.Delete"
                @click.stop="deleteRule(row.id)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section class="surface rule-editor-surface">
        <div class="surface-head">
          <div>
            <h2>数据库断言构建器</h2>
            <span>{{ selectedJob ? `当前 Job：${ruleJobOptionLabel(selectedJob)}` : '未选择 Job' }}</span>
          </div>
        </div>

        <el-form
          ref="ruleEditor"
          :model="ruleForm"
          :rules="ruleFormRules"
          class="rule-form"
          label-position="top"
          @submit.prevent
        >
          <el-form-item label="绑定 Job" prop="jobId">
            <el-select
              :model-value="ruleForm.jobId"
              class="rule-job-select"
              clearable
              filterable
              popper-class="rule-job-select-dropdown"
              placeholder="选择绑定 Job"
              @update:model-value="updateRuleField('jobId', $event)"
            >
              <el-option
                v-for="job in jobs"
                :key="job.id"
                :label="ruleJobOptionLabel(job)"
                :value="String(job.id)"
              />
            </el-select>
          </el-form-item>

          <section class="rule-query-parser">
            <el-input
              :model-value="ruleQuerySql"
              type="textarea"
              :rows="3"
              placeholder="SELECT * FROM t_biz_loan_apply WHERE user_id=165;"
              @update:model-value="ruleQuerySql = $event"
            />
            <el-button :icon="icons.Wand" @click="handleParseRuleQuery">解析 SQL</el-button>
          </section>

          <div class="rule-builder-grid">
            <el-form-item label="数据库" prop="database">
              <el-select
                :model-value="ruleForm.database"
                clearable
                filterable
                placeholder="选择数据库"
                @update:model-value="handleRuleDatabaseChange"
              >
                <el-option v-for="db in databases" :key="db" :label="db" :value="db" />
              </el-select>
            </el-form-item>
            <el-form-item label="表" prop="table">
              <el-select
                :model-value="ruleForm.table"
                clearable
                filterable
                placeholder="选择表"
                :disabled="!ruleForm.database"
                @update:model-value="handleRuleTableChange"
              >
                <el-option v-for="table in tables" :key="table" :label="table" :value="table" />
              </el-select>
            </el-form-item>
          </div>

          <div class="selected-rule-target">
            <span class="selected-rule-label">已选择表</span>
            <div v-if="selectedRulePathParts.length" class="rule-path">
              <span v-for="part in selectedRulePathParts" :key="part">{{ part }}</span>
            </div>
            <strong v-else>-</strong>
          </div>

          <div v-if="columns.length" class="field-chip-list">
            <el-button
              v-for="column in columns"
              :key="column.name"
              size="small"
              :type="ruleAssertionHasField(column.name) ? 'primary' : ''"
              :plain="!ruleAssertionHasField(column.name)"
              @click="addRuleAssertion(column.name)"
            >
              <span>{{ column.name }}</span>
              <small v-if="column.type">{{ column.type }}</small>
            </el-button>
          </div>

          <el-form-item label="筛选条件" prop="filter">
            <el-input
              :model-value="ruleForm.filter"
              placeholder="例：user_id = 165 AND status = 1"
              @update:model-value="updateRuleField('filter', $event)"
            />
          </el-form-item>

          <div class="rule-assertion-list">
            <div class="rule-assertion-head">
              <span>字段断言</span>
              <el-button size="small" :icon="icons.Plus" @click="addRuleAssertion()">
                添加断言
              </el-button>
            </div>
            <div
              v-for="(assertion, index) in ruleForm.assertions"
              :key="index"
              class="rule-assertion-row"
            >
              <el-select
                :model-value="assertion.field"
                clearable
                filterable
                placeholder="字段"
                @update:model-value="updateRuleAssertion(index, 'field', $event)"
              >
                <el-option
                  v-for="column in columns"
                  :key="column.name"
                  :label="columnLabel(column)"
                  :value="column.name"
                />
              </el-select>
              <el-select
                :model-value="assertion.operator"
                @update:model-value="updateRuleAssertion(index, 'operator', $event)"
              >
                <el-option
                  v-for="operator in dbRuleOperators"
                  :key="operator"
                  :label="operator"
                  :value="operator"
                />
              </el-select>
              <el-input
                :model-value="assertion.expected"
                placeholder="期望值"
                @update:model-value="updateRuleAssertion(index, 'expected', $event)"
              />
              <el-button
                type="danger"
                plain
                :icon="icons.Delete"
                @click="removeRuleAssertion(index)"
              />
            </div>
          </div>

          <el-form-item label="规则预览">
            <pre class="rule-preview">{{ rulePreview }}</pre>
          </el-form-item>

          <section v-if="ruleTestResult" class="rule-test-result">
            <div class="rule-test-result-head">
              <h3>Rule 测试结果</h3>
              <el-tag :type="ruleTestResult.ok ? 'success' : 'danger'" size="small" effect="plain">
                {{ ruleTestResult.ok ? '通过' : '失败' }}
              </el-tag>
            </div>
            <ul v-if="ruleTestChecks.length" class="rule-test-checks">
              <li v-for="(check, index) in ruleTestChecks" :key="index">
                <div>
                  <strong>{{ check.rule || '-' }}</strong>
                  <span>{{ ruleCheckDetail(check) }}</span>
                </div>
                <el-tag :type="check.ok ? 'success' : 'danger'" size="small" effect="plain">
                  {{ check.ok ? '通过' : '失败' }}
                </el-tag>
              </li>
            </ul>
            <div v-else class="flow-rule-empty">暂无断言结果</div>
          </section>

          <div class="form-actions">
            <el-button type="primary" :icon="icons.Select" @click="handleSaveRule">
              保存规则
            </el-button>
            <el-button
              :icon="icons.CaretRight"
              :loading="testingRuleForm"
              @click="handleTestRuleForm"
            >
              测试 Rule
            </el-button>
            <el-button @click="handleResetRuleForm">清空规则</el-button>
          </div>
        </el-form>
      </section>
    </div>
  </section>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { CaretRight, Delete, Edit, Plus, Refresh, Select, Share, MagicStick as Wand } from "@element-plus/icons-vue";
import { ruleApi } from "../api";
import {
  buildRuleText,
  normalizedRuleAssertions,
  parseDbAssertRule,
  parseSelectRuleQuery,
} from "../store/form-helpers";
import { formatDateTime, ruleJobOptionLabel } from "../utils/job-display";

export default {
  name: "RulesPage",

  data() {
    return {
      icons: {
        CaretRight,
        Delete,
        Edit,
        Plus,
        Refresh,
        Select,
        Share,
        Wand,
      },
      ruleFormRules: {
        jobId: [{ required: true, message: "请选择绑定 Job", trigger: "change" }],
        database: [{ required: true, message: "请选择数据库", trigger: "change" }],
        table: [{ required: true, message: "请选择表", trigger: "change" }],
        filter: [{ required: true, message: "请输入筛选条件", trigger: "blur" }],
      },
      ruleTestResult: null,
      ruleQuerySql: "",
      runningRuleId: null,
      testingRuleForm: false,
    };
  },

  computed: {
    ...mapState([
      "dbRuleOperators",
      "allRules",
      "jobs",
      "selectedJob",
      "rules",
      "databases",
      "tables",
      "columns",
      "ruleForm",
      "sharingRuleTemplateId",
    ]),

    selectedRuleFieldLabel() {
      const parts = [this.ruleForm.database, this.ruleForm.table].filter(Boolean);
      return parts.length ? parts.join(".") : "-";
    },

    selectedRulePathParts() {
      return [this.ruleForm.database, this.ruleForm.table].filter(Boolean);
    },

    rulePreview() {
      if (!this.ruleForm.database && !this.ruleForm.table && !this.validRuleAssertions().length) {
        return this.ruleForm.rule || "{}";
      }
      return JSON.stringify(
        {
          type: "db_assert",
          database: this.ruleForm.database || "",
          table: this.ruleForm.table || "",
          filter: this.ruleForm.filter || "",
          assertions: this.validRuleAssertions(),
        },
        null,
        2,
      );
    },

    ruleTestChecks() {
      return this.ruleTestResult && Array.isArray(this.ruleTestResult.checks)
        ? this.ruleTestResult.checks
        : [];
    },
  },

  mounted() {
    this.loadAllRules();
  },

  methods: {
    ...mapActions([
      "selectRuleJob",
      "saveRule",
      "deleteRule",
      "loadAllRules",
      "loadTables",
      "loadColumns",
      "shareRuleTemplate",
    ]),
    ...mapMutations({
      resetRuleFormState: "resetRuleForm",
      setRuleFormFieldMutation: "setRuleFormField",
      setRuleAssertionFieldMutation: "setRuleAssertionField",
      addRuleAssertionMutation: "addRuleAssertion",
      removeRuleAssertionMutation: "removeRuleAssertion",
      setRuleForm: "setRuleForm",
    }),
    formatDateTime,
    ruleJobOptionLabel,

    updateRuleField(field, value) {
      this.setRuleFormFieldMutation({ field, value });
    },

    updateRuleAssertion(index, field, value) {
      this.setRuleAssertionFieldMutation({ index, field, value });
    },

    addRuleAssertion(field = "") {
      this.addRuleAssertionMutation(field);
    },

    removeRuleAssertion(index) {
      this.removeRuleAssertionMutation(index);
    },

    async handleRuleDatabaseChange(value) {
      this.updateRuleField("database", value || "");
      this.updateRuleField("table", "");
      this.updateRuleField("assertions", [{ field: "", operator: "=", expected: "" }]);
      await this.loadTables(value || "");
    },

    async handleRuleTableChange(value) {
      this.updateRuleField("table", value || "");
      this.updateRuleField("assertions", [{ field: "", operator: "=", expected: "" }]);
      await this.loadColumns({
        database: this.ruleForm.database,
        table: value || "",
      });
    },

    async handleParseRuleQuery() {
      try {
        const parsed = parseSelectRuleQuery(this.ruleQuerySql);
        // SQL copied from the database console often omits the schema. Reuse
        // the selected schema, or the only visible schema, so the Rule can be
        // tested immediately after parsing.
        const database =
          parsed.database ||
          this.ruleForm.database ||
          (this.databases.length === 1 ? this.databases[0] : "");
        this.updateRuleField("database", database);
        this.updateRuleField("table", parsed.table);
        this.updateRuleField("filter", parsed.filter);
        if (parsed.assertions.length) {
          this.updateRuleField("assertions", parsed.assertions);
        }
        if (database) {
          await this.loadTables(database);
          await this.loadColumns({ database, table: parsed.table });
        }
        this.ruleTestResult = null;
        this.$store.dispatch("showToast", { message: database ? "SQL 已解析" : "SQL 已解析，请选择数据库" });
      } catch (error) {
        this.$store.dispatch("showToast", { message: error.message, isError: true });
      }
    },

    columnLabel(column) {
      return column.type ? `${column.name} (${column.type})` : column.name;
    },

    validRuleAssertions() {
      return normalizedRuleAssertions(this.ruleForm);
    },

    ruleAssertionHasField(field) {
      return Array.isArray(this.ruleForm.assertions)
        ? this.ruleForm.assertions.some((assertion) => assertion.field === field)
        : false;
    },

    async handleSaveRule() {
      try {
        await this.$refs.ruleEditor.validate();
      } catch (error) {
        return;
      }
      const assertions = this.validRuleAssertions();
      if (!assertions.length || assertions.some((assertion) => !assertion.field || !assertion.expected)) {
        this.$store.dispatch("showToast", { message: "请至少填写一条完整字段断言", isError: true });
        return;
      }
      await this.saveRule();
    },

    async handleTestSavedRule(rule) {
      this.runningRuleId = rule.id;
      try {
        await this.fillRuleForm(rule);
        const data = await ruleApi.run(rule.id);
        this.ruleTestResult = data.result;
        this.$store.dispatch("showToast", { message: "Rule 测试完成" });
      } catch (error) {
        this.$store.dispatch("showToast", { message: error.message, isError: true });
      } finally {
        this.runningRuleId = null;
      }
    },

    async handleTestRuleForm() {
      const assertions = this.validRuleAssertions();
      if (
        !this.ruleForm.database ||
        !this.ruleForm.table ||
        !this.ruleForm.filter ||
        !assertions.length ||
        assertions.some((assertion) => !assertion.field || !assertion.expected)
      ) {
        this.$store.dispatch("showToast", { message: "请先填写完整 Rule", isError: true });
        return;
      }

      this.testingRuleForm = true;
      try {
        const data = await ruleApi.test({ rule: buildRuleText(this.ruleForm) });
        this.ruleTestResult = data.result;
        this.$store.dispatch("showToast", { message: "Rule 测试完成" });
      } catch (error) {
        this.$store.dispatch("showToast", { message: error.message, isError: true });
      } finally {
        this.testingRuleForm = false;
      }
    },

    handleResetRuleForm() {
      this.resetRuleFormState();
      this.ruleTestResult = null;
      if (this.selectedJob) {
        this.updateRuleField("jobId", String(this.selectedJob.id));
      }
      this.$nextTick(() => {
        this.$refs.ruleEditor?.clearValidate();
      });
    },

    handleAddRule() {
      this.handleResetRuleForm();
    },

    handleRuleRowClick(row) {
      this.fillRuleForm(row);
    },

    async handleShareRule(id) {
      await this.shareRuleTemplate(id);
    },

    async fillRuleForm(rule) {
      this.ruleTestResult = null;
      const jobId = Number(rule.job_id);
      if (jobId && (!this.selectedJob || Number(this.selectedJob.id) !== jobId)) {
        await this.selectRuleJob(String(jobId));
      }
      this.setRuleForm(rule);
      if (this.ruleForm.database) {
        await this.loadTables(this.ruleForm.database);
      }
      if (this.ruleForm.database && this.ruleForm.table) {
        await this.loadColumns({
          database: this.ruleForm.database,
          table: this.ruleForm.table,
        });
      }
      this.$nextTick(() => {
        this.$refs.ruleEditor?.clearValidate();
      });
    },

    ruleRowClassName({ row }) {
      return Number(this.ruleForm.id) === Number(row.id) ? "selected-row" : "";
    },

    ruleJobLabel(row) {
      const description = row.job_step_desc || "未填写描述";
      return row.job_step ? `Step ${row.job_step} - ${description}` : description;
    },

    ruleTarget(row) {
      const rule = parseDbAssertRule(row.rule);
      if (!rule) {
        return "兼容文本规则";
      }
      return [rule.database, rule.table].filter(Boolean).join(".") || "-";
    },

    ruleTargetParts(row) {
      const rule = parseDbAssertRule(row.rule);
      return rule ? [rule.database, rule.table].filter(Boolean) : [];
    },

    ruleFilter(row) {
      const rule = parseDbAssertRule(row.rule);
      return rule ? rule.filter || "-" : "-";
    },

    ruleAssertion(row) {
      const rule = parseDbAssertRule(row.rule);
      if (!rule) {
        return "-";
      }
      const assertions = this.ruleAssertions(rule);
      return assertions.length
        ? assertions
            .map((assertion) => `${assertion.field} ${assertion.operator || "="} ${assertion.expected}`)
            .join("，")
        : "-";
    },

    ruleAssertions(rule) {
      if (Array.isArray(rule.assertions) && rule.assertions.length) {
        return rule.assertions.map((assertion) => ({
          field: assertion.field || "",
          operator: assertion.operator || "=",
          expected: assertion.expected === undefined || assertion.expected === null ? "" : assertion.expected,
        }));
      }
      return [
        {
          field: rule.field || "",
          operator: rule.operator || "=",
          expected: rule.expected === undefined || rule.expected === null ? "" : rule.expected,
        },
      ].filter((assertion) => assertion.field || assertion.expected);
    },

    ruleCheckDetail(check) {
      if (check.error) {
        return check.error;
      }
      return `实际 ${this.displayRuleValue(check.actual)}，期望 ${check.operator || "="} ${this.displayRuleValue(check.expected)}`;
    },

    displayRuleValue(value) {
      if (value === undefined) {
        return "undefined";
      }
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    },

    formatRuleContent(row) {
      const rule = parseDbAssertRule(row.rule);
      if (!rule) {
        return row.rule || "无规则内容";
      }
      return JSON.stringify(rule, null, 2);
    },
  },
};
</script>
